'use client';

import { Stack, Textarea, Alert, Text, Group, Progress } from '@mantine/core';
import { IconCheck, IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import classes from '../UnifiedMentorshipApplication.module.css';

export const MAX_MESSAGE_LENGTH = 1000;

interface ValidationResult {
  isValid: boolean;
  score: number;
  feedback: string;
  suggestions?: string[];
}

interface MessageStepProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  mentorName: string;
  validation?: ValidationResult | null;
  isValidating?: boolean;
}

export default function MessageStep({ 
  value, 
  onChange, 
  error, 
  mentorName,
  validation,
  isValidating
}: MessageStepProps) {
  const charCount = value.length;
  const charPercentage = (charCount / MAX_MESSAGE_LENGTH) * 100;
  const isNearLimit = charCount > MAX_MESSAGE_LENGTH * 0.9;
  const isOverLimit = charCount > MAX_MESSAGE_LENGTH;

  const handleChange = (newValue: string) => {
    // Don't allow exceeding limit
    if (newValue.length <= MAX_MESSAGE_LENGTH) {
      onChange(newValue);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" style={{ margin: 0 }}>
        Tell {mentorName} about yourself, your goals, and challenges:
      </Text>
      
      <Stack gap="xs">
        <Textarea
          placeholder={`Write a message to ${mentorName}...`}
          value={value}
          onChange={(e) => handleChange(e.currentTarget.value)}
          error={error || (isOverLimit && 'Message is too long')}
          className={classes.messageTextarea}
          autosize
          minRows={6}
          maxRows={10}
        />
        <Group justify="space-between" gap="xs">
          <Text size="xs" c={isNearLimit ? 'orange' : 'dimmed'}>
            {charCount} / {MAX_MESSAGE_LENGTH} characters
          </Text>
          {charCount > 0 && (
            <Progress 
              value={charPercentage} 
              size="xs" 
              style={{ flex: 1, maxWidth: 200 }}
              color={isOverLimit ? 'red' : isNearLimit ? 'orange' : 'blue'}
            />
          )}
        </Group>
      </Stack>
      
      {/* AI Validation Feedback */}
      {isValidating && (
        <Alert icon={<IconSparkles size={16} />} color="blue" variant="light">
          <Group gap="xs">
            <Text size="sm">Reviewing your message...</Text>
          </Group>
        </Alert>
      )}
      
      {validation && !isValidating && (
        <Alert 
          icon={validation.isValid ? <IconCheck size={16} /> : <IconAlertCircle size={16} />} 
          color={validation.isValid ? 'green' : 'yellow'}
          variant="light"
        >
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {validation.isValid ? 'Great message!' : 'Your message could be stronger'}
            </Text>
            <Text size="sm">
              {validation.feedback}
            </Text>
            {validation.suggestions && validation.suggestions.length > 0 && (
              <Stack gap={4} mt="xs">
                <Text size="sm" fw={500}>Suggestions:</Text>
                {validation.suggestions.map((suggestion, idx) => (
                  <Text key={idx} size="sm" c="dimmed">• {suggestion}</Text>
                ))}
              </Stack>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
